import React from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useDrag, useDrop } from "react-dnd";
import {
  setSelectedFormItemId,
  reorderElementReference,
  pushFormActionStack,
  reorderUndoElementReference,
  updatedSelectedeElements,
  clearSelectedItems,
} from "../../../../../stateManager/store/formCreation_v2/actions";
import { DeviceType, FormCreationData } from "../../../../../stateManager/store/formCreation_v2/types";
import { ItemTypes } from "../types/ItemTypes";
import { AppState } from "../../../../../stateManager/store";
import { getMargin } from "../helpers/generateStyles";
import { getDimensions } from "../helpers/getDimensions";
import { flexContainers, gridContainers } from "../helpers/handleContainers"
import { myRedoStack, myUndoStack } from "../stack/Stack";
import { setMultipleSelectionv2 } from "../../../../../stateManager/store/contextMenuv2/action";
import { conditionalElements, wrapperLessElements } from "../constants";
import { DeleteElement } from "./DeleteElement";

const ELEMENTS_WITH_NO_SCROLL = [
  "Button"
]

interface IElementWrapper {
  element: FormCreationData;
  parentElement?: FormCreationData;
  children: React.ReactNode;
  index: number;
  isCheckedIn?: any;
  isRootChildren?: boolean;
}

export const ElementWrapper: React.FC<IElementWrapper> = (props: any) => {
  const { selectedElementId, deviceType, selectedElementsId, isCoreV3 } = useSelector((state: AppState) => state.formJson);
  const { element, children, index, isCheckedIn } = props;
  const { id, parentId, type, isAsset, isChildAsset } = element;
  const dispatch = useDispatch();
  const [{ isDragging }, drag] = useDrag({
    item: { type: ItemTypes.ELEMENT, json: { ...element, index } },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: isCheckedIn && !isChildAsset,
  });

  const [{ isOverCurrent, canDrop }, drop] = useDrop({
    accept: [
      ItemTypes.ELEMENT,
    ],
    canDrop: (item: any) => {
      // 1. to prevent the source element from dropping into itself
      if (id === item.json.id) {
        return false
      }

      // 2. only sibling elements can be dropped
      return element.parentId === item.json.parentId;
    },
    collect: (monitor) => {
      return {
        isOverCurrent: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      };
    },
    drop: (item: any) => {
      if (isOverCurrent) {
        const { json } = item
        const dropTarget = {
          index,
          id,
          parentId
        }
        const dragSource = {
          id: json.id,
          index: json.index,
          parentId: json.parentId,
        }
        // undoredo for drag and drop
        const payload: any = { dropTarget, dragSource }
        myUndoStack.push({ ...payload });
        dispatch(pushFormActionStack({
          undo: () => {
            const poppedElement = myUndoStack.pop();
            if (poppedElement) {
              myRedoStack.push(poppedElement);
              dispatch(reorderUndoElementReference({
                dropTarget: {
                  index: poppedElement.dragSource.index,
                  id: "",
                  parentId: poppedElement.dragSource.parentId,
                },
                dragSource: {
                  ...poppedElement.dragSource,
                  index: poppedElement.dropTarget.index,
                  parentId: poppedElement.dropTarget.parentId,
                },
                increment: -1
              }))
            }
          },
          redo: () => {
            const poppedElement = myRedoStack.pop();
            if (poppedElement) {
              myUndoStack.push({ ...poppedElement });
              dispatch(reorderUndoElementReference({
                dropTarget: poppedElement.dropTarget,
                dragSource: poppedElement.dragSource,
                increment: 1
              }))
            }
          }
        }));

        dispatch(reorderElementReference({
          dropTarget,
          dragSource,
        }))
      }
    },
  });

  const handleSelect = (e: React.MouseEvent<HTMLDivElement>, id: any) => {
    e.stopPropagation();
    if (e.shiftKey && !isChildAsset) {
      dispatch(setMultipleSelectionv2(true));
      dispatch(updatedSelectedeElements(id));
    } else {
      dispatch(setMultipleSelectionv2(false));
      dispatch(
        setSelectedFormItemId({
          id,
          isAssetInstanceSelected: isAsset || isChildAsset,
        })
      );
      dispatch(clearSelectedItems())
    }
  };

  const handleRightClick = async (e: React.MouseEvent<HTMLDivElement>, id: any) => {
    e.stopPropagation();
    const existingWrapper = document.getElementById("wrapper");
    existingWrapper && await existingWrapper.remove();
    await dispatch(
      setSelectedFormItemId({
        id,
        isAssetInstanceSelected: isAsset || isChildAsset,
      })
    );
    await dispatch(clearSelectedItems());
  };

  /* styles */
  const style = element?.properties?.styles || {};
  const marginStyle = getMargin(style[deviceType])
  const dimensionStyle: React.CSSProperties = {
    minHeight: `${getDimensions(style[deviceType]?.height, style[deviceType]?.heightMode)}`,
    width: `${getDimensions(style[deviceType]?.width, style[deviceType]?.widthMode)}`,
    top: `${style[deviceType]?.top ? `${style[deviceType]?.top}px` : undefined}`,
    left: `${style[deviceType]?.left ? `${style[deviceType]?.left}px` : undefined}`,
    bottom: `${style[deviceType]?.bottom ? `${style[deviceType]?.bottom}px` : undefined}`,
    right: `${style[deviceType]?.right ? `${style[deviceType]?.right}px` : undefined}`,
    overflowY: flexContainers.includes(type) ? 'auto' : 'hidden'
  }
  const getSelectedElementStyle = (
    id: string,
  ): React.CSSProperties => {
    const opacity = isDragging ? 0.4 : 1;
    const payload = {
      opacity,
      cursor: "pointer",
      ...dimensionStyle,
      borderColor: '#FE4365'
    }

    // show border if element is selected
    if (selectedElementId === id && type !== "FAB") {
      payload.zIndex = 100
      payload.boxShadow = "rgb(218 218 218 / 90%) 0px 0px 1px 1px inset, rgb(218 218 218 / 70%) 5px 0px 34px -5px";
      payload.border = " 1.65px solid #FE4365"
      payload.borderRadius = "4px"
      payload.padding = "2px"
    }

    // show border if element is multi-selected
    if (selectedElementsId.includes(id)) {
      payload.zIndex = 100
      payload.border = " 1.65px solid #FE4365"
      payload.borderRadius = "4px"
      payload.padding = "8px"
    }
    const isParentGrid = !props.parentElement || gridContainers.includes(props.parentElement.type)

    if (!isParentGrid) {
      payload.flexGrow = style[deviceType]?.flexGrow || (element.type === 'Spacer' ? 1 : 0)
      payload.flexShrink = style[deviceType]?.flexShrink || (element.type === 'Spacer' ? 1 : 0)
    }


    return payload;
  };
  function getFontSize(deviceType: DeviceType): string {
    switch (deviceType) {
      case "Lg":
        return "text-xl";
      case "Md":
        return "text-base";
      default:
        return "text-xs";
    }
  }
  const isParentFlex = (props.parentElement && props.parentElement.type === 'Column') || (props.parentElement && props.parentElement.type === 'Row')

  const gridSpanClasses = `col-span-${style[deviceType]?.span  || (deviceType === "Sm" ? 1 : deviceType === "Md" ? 6 : 12)} row-span-${style[deviceType]?.rowSpan}`;
  // const xAlignGen = !isParentFlex ? `justify-self-${style[deviceType]?.alignX}` : '';
  const xAlignGen = !isParentFlex ? 
                      `${style[deviceType]?.alignX === "start" ? 'mr-auto' : ''}
                      ${style[deviceType]?.alignX === "center" ? 'mx-auto' : ''}
                      ${style[deviceType]?.alignX === "end" ? 'ml-auto' : ''}` : '';
  const yAlignGen = !isParentFlex ? `self-${style[deviceType]?.alignY}` : '';
  const visibilityGen = `${style[deviceType]?.visible === true ? "block" : style[deviceType]?.visible ? "block" : "hidden"
    }`;
  const positionGen = `${style[deviceType]?.absolute ? "absolute" : "relative"
    }`;

  const isActive = isOverCurrent && canDrop;
  const isElementFixed = style[deviceType]?.fixed

  if (isElementFixed && !isCoreV3) {
    const fixedRoot = document.getElementById('canvasFixedElement')
    if (fixedRoot) {
      fixedRoot.style.padding = marginStyle.margin
      fixedRoot.style.paddingTop = marginStyle.marginTop
      fixedRoot.style.paddingRight = marginStyle.marginRight
      fixedRoot.style.paddingBottom = marginStyle.marginBottom
      fixedRoot.style.paddingLeft = marginStyle.marginLeft;

      return ReactDOM.createPortal((
        <div
          key={id}
          className={`min-h overflow-x-auto ${isActive && 'drop-active'} ${gridSpanClasses} ${xAlignGen} ${yAlignGen} ${visibilityGen} ${flexContainers.includes(type) && 'hide-scrollbar'}`}
          style={getSelectedElementStyle(id)}
          onClick={(e) => handleSelect(e, id)}
          onContextMenu={(e) => {
            e.preventDefault();
            handleRightClick(e,id);
          }}
        >
          {children}
          {isCheckedIn && <>
            {selectedElementId === id && type !== "FAB" && type !== "SnackBar" && type !== "A8Modal" && type !== "CustomModal" && 
            <p data-test-id={id}
            id={`tag_${id}`}
            style={{marginTop:"-10px",
          }} 
            className={`${getFontSize(deviceType)} absolute bg-palered flex items-center text-sm text-white px-1 rounded h-6 bottom-0 z-50 left-0`}>{selectedElementId}</p>}
            <DeleteElement {...element} selectedElementId={selectedElementId} />
          </>}
        </div>
      ), fixedRoot)
    }
    return null
  }


  if (conditionalElements.includes(type)) {
    return null
  }

  if (wrapperLessElements.includes(type)) {
    return (
      <div
        key={id}
        ref={ref => drag(drop(ref))}
        className={`
          min-h overflow-x-auto py-2 mt-1
          ${selectedElementId !== id && type !== "FAB" ? "hover:bg-bluebg" : ""}
          ${isActive && 'drop-active'}
          ${positionGen}
          ${gridSpanClasses}
          ${xAlignGen}
          ${yAlignGen}
          ${visibilityGen}
          ${flexContainers.includes(type) && 'hide-scrollbar'}
          ${selectedElementId === id && element.type !== "FAB" ? "border selected-border border-solid border-palered rounded-sm" : ""}
        `}
        style={{ ...getSelectedElementStyle(id), borderColor: "yellow", ...marginStyle }}
        onClick={(e) => handleSelect(e, id)}
        onContextMenu={(e) => {
          e.preventDefault();
          handleRightClick(e,id);
        }}
      >
        {/* {selectedElementId === id ? <p className="absolute bg-palered text-sm text-white px-1 rounded-sm top-0 z-50">{element.type}</p> : null} */}
        {children}
        {isCheckedIn && !isChildAsset && <DeleteElement {...element} selectedElementId={selectedElementId} />}
      </div>
    )
  }
const selectedElId = getSelectedElementStyle(id);
  return (
    <div className={`${positionGen} ${gridSpanClasses} ${visibilityGen}
      ${xAlignGen}
      ${yAlignGen}
    `} style={{
      left: dimensionStyle.left,
      right: dimensionStyle.right,
      top: dimensionStyle.top,
      bottom: dimensionStyle.bottom,
      width: dimensionStyle.width,
      height: dimensionStyle.height,
      flexGrow: selectedElId?.flexGrow ? Number(selectedElId.flexGrow) : "unset",
      flexShrink: selectedElId?.flexShrink ? Number(selectedElId.flexShrink) : "unset",
      ...marginStyle,   
    }}>
      <div
        key={id}
        ref={ref => drag(drop(ref))}
        className={`min-h 
          ${ELEMENTS_WITH_NO_SCROLL.includes(element.type) ? "overflow-hidden" : "overflow-x-auto"} 
          ${isActive && 'drop-active'} 
          ${gridSpanClasses} 
          ${flexContainers.includes(type) && 'hide-scrollbar'}`}
        style={{...getSelectedElementStyle(id), width: "100%", height: "100%" }}
        onClick={(e) => handleSelect(e, id)}
        onContextMenu={(e) => {
          e.preventDefault();
          handleRightClick(e,id);
        }}
      >
        {children}
        {isCheckedIn && !isChildAsset && 
        <>
        {selectedElementId === id && <p style={{marginTop:"-10px",
        // background:"hotpink",position: "absolute", bottom: "0px", zIndex: 1000, fontWeight: 700, color: '#fff', border: "1.65px solid #FE4365",height: "25px"
      }} 
        className={`${getFontSize(deviceType)} absolute bg-palered flex items-center text-sm text-white px-1 rounded h-6 bottom-0 z-50 left-0`}>{selectedElementId}</p>}
        <DeleteElement {...element} selectedElementId={selectedElementId} />
        </>
        }
      </div>
    </div>
  );
};
